import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  Loader,
  Paper,
  RangeSlider,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { MultiViewer } from "@multiconsult-group/multiplacer-ui";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  useGetMcbimFileFloorVersionsAllQuery,
  useGetMcbimFragsQuery,
  usePostMcbimItemsQuery,
} from "../../openapi";
import { selectIsAnyRtkQueryRequestPending } from "../apiSelectors";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { McBimItemDetails } from "../components/McBimItem/McBimItemDetails";
import { useProjectId } from "../hooks/useProjectId";
import {
  selectClickedMcBimItemId,
  setClickedMcBimItemIdInRedux,
} from "../ViewerRedux/viewerSlice";
import {
  selectProjectViewerAllFileFloorKeys,
  selectProjectViewerFileFloorVersionCount,
  selectProjectViewerFileOptions,
  selectProjectViewerMcbimItemsQueryArgs,
  selectProjectViewerPixPolygons,
  selectProjectViewerSelectedFileFloorKeySet,
  selectProjectViewerTransformPivot,
  selectProjectViewerVisibleMcbimFileNames,
  selectProjectViewerZoomExtentKey,
  selectProjectViewerZBoundsM,
  type IProjectViewerFileOption,
  type IProjectViewerMcbimItemsQueryArg,
} from "./projectViewerSelectors";
import {
  clearSelectedFileFloorKeys,
  selectProjectViewerSelectedFileFloorKeys,
  selectProjectViewerZClipRangeM,
  setSelectedFileFloorKeys,
  setProjectViewerZClipRangeM,
  toggleSelectedFileFloorKey,
  updateProjectViewerZClipRangeM,
} from "./projectViewerSlice";
import { JordningGeneratorModal } from "./Jordning";

const checkboxStyles = {
  root: { width: "100%" },
  body: { width: "100%", minWidth: 0, alignItems: "flex-start" },
  labelWrapper: { flex: 1, minWidth: 0 },
  label: { width: "100%" },
};

interface IProjectViewerMcbimItemsLoaderProps {
  queryArg: IProjectViewerMcbimItemsQueryArg;
}

const ProjectViewerMcbimItemsLoader: React.FC<IProjectViewerMcbimItemsLoaderProps> = ({
  queryArg,
}) => {
  usePostMcbimItemsQuery(queryArg);
  return null;
};

export const ProjectViewer: React.FC = () => {
  const [jordningModalOpen, setJordningModalOpen] = useState(false);
  const dispatch = useAppDispatch();
  const projectNo = useProjectId();
  const canLoadProject = Number.isFinite(projectNo) && projectNo > 0;
  const selectedFileFloorKeys = useAppSelector(selectProjectViewerSelectedFileFloorKeys);
  const zClipRangeM = useAppSelector(selectProjectViewerZClipRangeM);
  const selectedFileFloorKeySet = useAppSelector(selectProjectViewerSelectedFileFloorKeySet);
  const fileOptions = useAppSelector(selectProjectViewerFileOptions);
  const allFileFloorKeys = useAppSelector(selectProjectViewerAllFileFloorKeys);
  const fileFloorVersionCount = useAppSelector(selectProjectViewerFileFloorVersionCount);
  const zBoundsM = useAppSelector(selectProjectViewerZBoundsM);
  const mcbimItemsQueryArgs = useAppSelector(selectProjectViewerMcbimItemsQueryArgs);
  const pixPolygons = useAppSelector(selectProjectViewerPixPolygons);
  const transformPivot = useAppSelector(selectProjectViewerTransformPivot);
  const visibleMcbimFileNames = useAppSelector(selectProjectViewerVisibleMcbimFileNames);
  const zoomExtentKey = useAppSelector(selectProjectViewerZoomExtentKey);
  const isAnyRtkQueryRequestPending = useAppSelector(selectIsAnyRtkQueryRequestPending);
  const clickedMcBimItemId = useAppSelector(selectClickedMcBimItemId);

  const { isLoading, isFetching, isError } = useGetMcbimFileFloorVersionsAllQuery(
    canLoadProject ? { projectNo } : skipToken
  );
  const {
    data: fragData,
    error: fragFilesError,
    isFetching: isFetchingFragFiles,
    isLoading: isLoadingFragFiles,
  } = useGetMcbimFragsQuery(canLoadProject ? { projectNo } : skipToken);

  const toggleFileFloors = (file: IProjectViewerFileOption) => {
    if (isAnyRtkQueryRequestPending) return;

    const fileFloorKeys = file.floors.map((floor) => floor.selectionKey);

    if (file.checked) {
      dispatch(
        setSelectedFileFloorKeys(
          selectedFileFloorKeys.filter((fileFloorKey) => !fileFloorKeys.includes(fileFloorKey))
        )
      );
      return;
    }

    dispatch(setSelectedFileFloorKeys([...selectedFileFloorKeys, ...fileFloorKeys]));
  };

  useEffect(() => {
    const { minM, maxM } = zBoundsM;
    if (minM == null || maxM == null) return;

    const nextBottomM =
      zClipRangeM.bottomM == null ? minM : Math.min(Math.max(zClipRangeM.bottomM, minM), maxM);
    const nextTopM =
      zClipRangeM.topM == null ? maxM : Math.min(Math.max(zClipRangeM.topM, minM), maxM);

    const nextRange =
      nextTopM <= nextBottomM
        ? { bottomM: minM, topM: maxM }
        : { bottomM: nextBottomM, topM: nextTopM };

    if (nextRange.bottomM === zClipRangeM.bottomM && nextRange.topM === zClipRangeM.topM) return;

    dispatch(setProjectViewerZClipRangeM(nextRange));
  }, [dispatch, zBoundsM, zClipRangeM.bottomM, zClipRangeM.topM]);

  const clipZMm = useMemo(() => {
    const { bottomM, topM } = zClipRangeM;
    if (bottomM == null || topM == null || topM <= bottomM) return null;

    return {
      bottom: Math.round(bottomM * 1000),
      top: Math.round(topM * 1000),
    };
  }, [zClipRangeM]);

  const resetZClipRange = () => {
    if (zBoundsM.minM == null || zBoundsM.maxM == null) return;

    dispatch(
      setProjectViewerZClipRangeM({
        bottomM: zBoundsM.minM,
        topM: zBoundsM.maxM,
      })
    );
  };

  const hasZBounds = zBoundsM.minM != null && zBoundsM.maxM != null;
  const zSliderMinM = zBoundsM.minM ?? null;
  const zSliderMaxM = zBoundsM.maxM ?? null;
  const zSliderValue: [number, number] | null =
    hasZBounds && zSliderMinM != null && zSliderMaxM != null
      ? [
          Math.min(Math.max(zClipRangeM.bottomM ?? zSliderMinM, zSliderMinM), zSliderMaxM),
          Math.min(Math.max(zClipRangeM.topM ?? zSliderMaxM, zSliderMinM), zSliderMaxM),
        ]
      : null;

  const pixPolygonEntityIdById = useMemo(() => {
    const map = new Map<string, string>();
    for (const polygon of pixPolygons) {
      if (typeof polygon.entityId === "string") {
        map.set(polygon.id, polygon.entityId);
      }
    }
    return map;
  }, [pixPolygons]);

  const twoDViewerProps = useMemo(
    () => ({
      polygons: pixPolygons,
      zoomExtentKey,
      transformPivot,
      flip2dCoordinate: true,
      useLeftClickToPan: true,
      onClick: (id: string | undefined) => {
        dispatch(setClickedMcBimItemIdInRedux(id ? (pixPolygonEntityIdById.get(id) ?? null) : null));
      },
    }),
    [dispatch, pixPolygons, pixPolygonEntityIdById, transformPivot, zoomExtentKey]
  );

  const threeDViewerProps = useMemo(
    () => ({
      fragFiles: fragData?.files ?? [],
      isLoadingFragFiles,
      isFetchingFragFiles,
      fragFilesError,
      visibleMcbimFileNames,
      clipZMm,
    }),
    [
      clipZMm,
      fragData?.files,
      fragFilesError,
      isFetchingFragFiles,
      isLoadingFragFiles,
      visibleMcbimFileNames,
    ]
  );

  return (
    <Box h="100%" w="100%" p="md" style={{ overflow: "hidden" }}>
      {mcbimItemsQueryArgs.map((queryArg) => (
        <ProjectViewerMcbimItemsLoader
          key={`${queryArg.body.fileName}-${queryArg.body.buildingStoryGlobalId}`}
          queryArg={queryArg}
        />
      ))}
      <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
        <Group justify="space-between" align="center" style={{ flexShrink: 0 }}>
          <Box>
            <Title order={3}>Prosjektviser</Title>
            <Text size="sm" c="dimmed">
              Prosjekt {projectNo}
            </Text>
          </Box>

          <Group gap="xs">
            {(isLoading || isFetching) && <Loader size="sm" />}
            <Button size="xs" variant="light" onClick={() => setJordningModalOpen(true)}>
              Generer jordning
            </Button>
            <Badge variant="light">{mcbimItemsQueryArgs.length} elementkall</Badge>
            <Badge variant="light">{pixPolygons.length} polygoner</Badge>
          </Group>
        </Group>
        <JordningGeneratorModal
          opened={jordningModalOpen}
          onClose={() => setJordningModalOpen(false)}
        />

        {isError ? (
          <Alert color="red" title="Kunne ikke hente fil- og etasjeindeks">
            Prøv igjen om en stund.
          </Alert>
        ) : (
          <Group
            align="stretch"
            gap="md"
            wrap="nowrap"
            w="100%"
            style={{ flex: 1, minHeight: 0, minWidth: 0 }}
          >
            <Paper
              withBorder
              radius="sm"
              p="sm"
              style={{
                flex: "0 0 430px",
                maxWidth: "430px",
                minHeight: 0,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Stack gap="sm" h="100%" style={{ minHeight: 0 }}>
                <Group justify="space-between" gap="xs" style={{ flexShrink: 0 }}>
                  <Text fw={600} size="sm">
                    Filer og etasjer
                  </Text>
                  <Badge variant="light" size="sm">
                    {selectedFileFloorKeys.length}/{allFileFloorKeys.length}
                  </Badge>
                </Group>

                <Group gap="xs" style={{ flexShrink: 0 }}>
                  <Button
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => dispatch(clearSelectedFileFloorKeys())}
                  >
                    Tøm
                  </Button>
                </Group>

                <Stack gap={6} style={{ flexShrink: 0 }}>
                  <Group justify="space-between" gap="xs">
                    <Text fw={600} size="sm">
                      Z-høyde
                    </Text>
                    <Button
                      size="xs"
                      variant="subtle"
                      color="gray"
                      disabled={!hasZBounds}
                      onClick={resetZClipRange}
                    >
                      Reset
                    </Button>
                  </Group>
                  {zSliderValue && zSliderMinM != null && zSliderMaxM != null ? (
                    <>
                      <RangeSlider
                        min={zSliderMinM}
                        max={zSliderMaxM}
                        step={0.1}
                        minRange={0.1}
                        value={zSliderValue}
                        disabled={zSliderMaxM <= zSliderMinM}
                        label={(value) => `${value.toFixed(1)} m`}
                        onChange={([bottomM, topM]) =>
                          dispatch(updateProjectViewerZClipRangeM({ bottomM, topM }))
                        }
                      />
                      <Group justify="space-between" gap="xs">
                        <Text size="xs" c="dimmed">
                          {zSliderValue[0].toFixed(1)} m
                        </Text>
                        <Text size="xs" c="dimmed">
                          {zSliderValue[1].toFixed(1)} m
                        </Text>
                      </Group>
                    </>
                  ) : (
                    <Text size="xs" c="dimmed">
                      Venter på z-høyder fra valgte filer.
                    </Text>
                  )}
                </Stack>

                <Stack gap="sm" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
                  {fileOptions.map((file) => {
                    return (
                      <Stack key={file.fileName} gap={6}>
                        <Checkbox
                          styles={checkboxStyles}
                          disabled={isAnyRtkQueryRequestPending}
                          checked={file.checked}
                          indeterminate={file.indeterminate}
                          onChange={() => toggleFileFloors(file)}
                          label={
                            <Group justify="space-between" gap="xs" wrap="nowrap" w="100%">
                              <Box style={{ flex: "1 1 auto", minWidth: 0 }}>
                                <Text size="sm" fw={600} truncate>
                                  {file.fileName}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                  {file.selectedFloorCount}/{file.floors.length} etasjer
                                </Text>
                              </Box>
                              <Badge
                                size="xs"
                                variant="light"
                                color="gray"
                                style={{ flexShrink: 0 }}
                              >
                                {file.rowCount}
                              </Badge>
                            </Group>
                          }
                        />

                        <Stack gap={4} pl="lg">
                          {file.floors.map((floor) => (
                            <Checkbox
                              styles={checkboxStyles}
                              key={floor.selectionKey}
                              size="xs"
                              disabled={isAnyRtkQueryRequestPending}
                              checked={selectedFileFloorKeySet.has(floor.selectionKey)}
                              onChange={() =>
                                !isAnyRtkQueryRequestPending &&
                                dispatch(toggleSelectedFileFloorKey(floor.selectionKey))
                              }
                              label={
                                <Group justify="space-between" gap="xs" wrap="nowrap" w="100%">
                                  <Box style={{ flex: "1 1 auto", minWidth: 0 }}>
                                    <Text size="sm" truncate>
                                      {floor.name}
                                    </Text>
                                    <Text size="xs" c="dimmed" truncate>
                                      {floor.building}
                                    </Text>
                                  </Box>
                                  <Badge
                                    size="xs"
                                    variant="light"
                                    color="gray"
                                    style={{ flexShrink: 0 }}
                                  >
                                    {floor.rowCount}
                                  </Badge>
                                </Group>
                              }
                            />
                          ))}
                        </Stack>

                        <Divider />
                      </Stack>
                    );
                  })}

                  {fileOptions.length === 0 && !isLoading && (
                    <Text size="sm" c="dimmed">
                      Ingen filer funnet.
                    </Text>
                  )}
                </Stack>
              </Stack>
            </Paper>

            <Paper
              withBorder
              radius="sm"
              style={{ overflow: "hidden", flex: 1, minWidth: 0, minHeight: 0 }}
            >
              {fileFloorVersionCount === 0 && !isLoading ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Ingen filer eller etasjer funnet for prosjektet.
                </Text>
              ) : mcbimItemsQueryArgs.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl">
                  Velg en eller flere etasjer.
                </Text>
              ) : (
                <MultiViewer
                  twoD={twoDViewerProps}
                  threeD={threeDViewerProps}
                />
              )}
            </Paper>

            {clickedMcBimItemId && (
              <Paper
                withBorder
                radius="sm"
                style={{
                  flex: "0 0 430px",
                  maxWidth: "430px",
                  minHeight: 0,
                  overflow: "hidden",
                }}
              >
                <McBimItemDetails />
              </Paper>
            )}
          </Group>
        )}
      </Stack>
    </Box>
  );
};
